// eslint-disable-next-line no-unused-vars
import { motion } from 'framer-motion';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const AVATAR_COLORS = [
  { bg: 'bg-blue-100', text: 'text-blue-700' },
  { bg: 'bg-emerald-100', text: 'text-emerald-700' },
  { bg: 'bg-amber-100', text: 'text-amber-700' },
  { bg: 'bg-rose-100', text: 'text-rose-700' },
  { bg: 'bg-violet-100', text: 'text-violet-700' },
  { bg: 'bg-cyan-100', text: 'text-cyan-700' },
  { bg: 'bg-orange-100', text: 'text-orange-700' },
  { bg: 'bg-pink-100', text: 'text-pink-700' },
  { bg: 'bg-teal-100', text: 'text-teal-700' },
  { bg: 'bg-indigo-100', text: 'text-indigo-700' },
];

function getAvatarColor(name = '') {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitials(firstName, lastName) {
  const first = (firstName || '')[0] || '';
  const last = (lastName || '')[0] || '';
  return (first + last).toUpperCase() || '?';
}

// ---------------------------------------------------------------------------
// KioskConfirmScreen
// ---------------------------------------------------------------------------
// "Is this you?" confirmation screen shown after identification.
//
// Props:
//   user      (object)   - { id, display_name, first_name, last_name, avatar_url, department }
//   onConfirm (function) - confirmed, proceed to clock action
//   onReject  (function) - not me, go back to identify
// ---------------------------------------------------------------------------

export default function KioskConfirmScreen({ selectedUser, confirmUser, rejectUser, deviceInfo, clockedIn }) {
  if (!selectedUser) return null;

  const user = selectedUser;
  const displayName =
    user.display_name ||
    `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
    'Unknown';

  const color = getAvatarColor(
    `${user.first_name || ''}${user.last_name || ''}`,
  );
  const initials = getInitials(user.first_name, user.last_name);

  return (
    <motion.div
      className="flex-1 flex flex-col items-center justify-center px-6 gap-8"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
    >
      {/* Greeting */}
      <motion.h2
        className="text-3xl font-bold text-slate-800"
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.3 }}
      >
        Hello!
      </motion.h2>

      {/* Avatar */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.15, duration: 0.4, ease: 'easeOut' }}
      >
        {user.avatar_url ? (
          <img
            src={user.avatar_url}
            alt={displayName}
            className="w-30 h-30 rounded-full object-cover shadow-lg ring-4 ring-white"
          />
        ) : (
          <div
            className={`w-30 h-30 rounded-full flex items-center justify-center text-4xl font-bold shadow-lg ring-4 ring-white ${color.bg} ${color.text}`}
          >
            {initials}
          </div>
        )}
      </motion.div>

      {/* Name & department */}
      <motion.div
        className="text-center"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25, duration: 0.3 }}
      >
        <p className="text-3xl font-semibold text-slate-800">{displayName}</p>
        {user.department && (
          <p className="mt-1 text-xl text-slate-500">{user.department}</p>
        )}
      </motion.div>

      {/* Action buttons */}
      <motion.div
        className="flex gap-4 mt-4"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35, duration: 0.3 }}
      >
        {/* Not Me -- secondary/outline */}
        <motion.button
          type="button"
          className="h-16 px-10 rounded-xl text-xl font-semibold border-2 border-slate-300 text-slate-600 bg-white hover:bg-slate-50 active:bg-slate-100 transition-colors shadow-sm"
          whileTap={{ scale: 0.95 }}
          onClick={rejectUser}
          aria-label="Not me, go back"
        >
          Not Me
        </motion.button>

        {/* That's Me -- primary / green tint */}
        <motion.button
          type="button"
          className="h-16 px-10 rounded-xl text-xl font-semibold bg-emerald-600 text-white hover:bg-emerald-700 active:bg-emerald-800 transition-colors shadow-md"
          whileTap={{ scale: 0.95 }}
          onClick={() => confirmUser(!!deviceInfo?.settings?.require_selfie && !clockedIn)}
          aria-label="Yes, that is me"
        >
          That's Me!
        </motion.button>
      </motion.div>
    </motion.div>
  );
}
